// The transcript as the feed sees it: one flat list of items, built from stored history
// (GET /api/projects/<id>) and then advanced by live stream events. Items:
//   user       {content, attachments}
//   assistant  {entry_id, content, ts, t1}          reasoning likewise
//   tool       {entry_id, kind, tool_name, short, label, settled, status, path, command,
//               diff, preview, output, url, query, elapsed_ms, ts}
//   callback   {callback_id, kind, detail, answered}
//   system / error {content}
// Every item carries `run` so the feed can tell which turn is live.

const STEP_FIELDS = ['short', 'status', 'label', 'settled', 'path', 'command', 'diff', 'preview',
  'output', 'url', 'query', 'elapsed_ms'];

const toMs = (v) => (v ? (typeof v === 'number' ? v : Date.parse(v.endsWith('Z') ? v : v + 'Z')) : 0);

export function fromHistory(messages) {
  return (messages || []).map((m) => {
    const meta = m.meta || {};
    const it = { key: 'h' + m.id, role: m.role, content: m.content || '', run: m.run,
      entry_id: m.entry_id || meta.entry_id, ts: meta.ts || toMs(m.created_at), t1: meta.t1 };
    if (m.role === 'user') { it.attachments = meta.attachments || []; it.author = m.author; }
    if (m.role === 'tool') {
      it.tool_name = m.tool_name || meta.tool_name;
      it.kind = meta.kind;
      for (const k of STEP_FIELDS) if (meta[k] !== undefined) it[k] = meta[k];
    }
    return it;
  });
}

function upsert(list, idx, patch) {
  const out = list.slice();
  out[idx] = { ...list[idx], ...patch };
  return out;
}

export function applyEvent(list, ev) {
  const t = ev.type;
  const byEntry = (eid) => (eid ? list.findIndex((m) => m.entry_id === eid) : -1);
  switch (t) {
    case 'user':
      return [...list, { key: 'u' + ev.seq, role: 'user', content: ev.content, attachments: ev.attachments || [],
        run: ev.run_id || ev.run, ts: ev.ts, author: ev.author, author_name: ev.author_name }];
    case 'assistant':
    case 'reasoning': {
      const i = byEntry(ev.entry_id);
      if (i < 0) {
        return [...list, { key: ev.entry_id, role: t, entry_id: ev.entry_id, run: ev.run,
          content: ev.content || ev.delta || '', ts: ev.ts, t1: ev.ts }];
      }
      const cur = list[i];
      let content = cur.content || '';
      if (ev.replace || ev.start) content = ev.content || '';
      else if (ev.delta) content += ev.delta;
      return upsert(list, i, { content, t1: ev.ts });
    }
    case 'tool': {
      const i = byEntry(ev.entry_id);
      const item = { key: ev.entry_id, role: 'tool', entry_id: ev.entry_id, run: ev.run, ts: ev.ts,
        tool_name: ev.tool_name, kind: ev.kind, short: ev.short, status: ev.status || 'running' };
      return i < 0 ? [...list, item] : upsert(list, i, item);
    }
    case 'tool_update': {
      const i = byEntry(ev.entry_id);
      if (i < 0) return list;
      const patch = {};
      for (const k of [...STEP_FIELDS, 'kind']) if (ev[k] !== undefined && ev[k] !== null && ev[k] !== '') patch[k] = ev[k];
      return upsert(list, i, patch);
    }
    case 'callback':
      return [...list, { key: 'c' + ev.callback_id, role: 'callback', run: ev.run, callback_id: ev.callback_id,
        kind: ev.kind, detail: ev.detail || {}, answered: false }];
    case 'system':
      return [...list, { key: 's' + ev.seq, role: 'system', content: ev.content, run: ev.run }];
    case 'error':
      return [...list, { key: 'e' + ev.seq, role: 'error', content: ev.content, code: ev.code || null, run: ev.run }];
    case 'run_finished': {
      // a step still "running" when the run ends did not finish
      return list.map((m) => (m.run === ev.run_id && m.role === 'tool' && m.status === 'running'
        ? { ...m, status: ev.status === 'cancelled' ? 'cancelled' : 'completed' } : m));
    }
    default:
      return list;
  }
}

export function markAnswered(list, callbackId) {
  return list.map((m) => (m.role === 'callback' && m.callback_id === callbackId ? { ...m, answered: true } : m));
}

// Group the flat list into turns: a user message and everything the agent did after it.
export function toTurns(list) {
  const turns = [];
  let cur = null;
  for (const it of list) {
    if (it.role === 'user') {
      cur = { key: it.key, user: it, agent: [], run: it.run };
      turns.push(cur);
    } else {
      if (!cur) { cur = { key: 'pre-' + it.key, user: null, agent: [], run: it.run }; turns.push(cur); }
      cur.agent.push(it);
      if (cur.run == null) cur.run = it.run;
    }
  }
  return turns;
}

// A short line the agent says on its way to the next step ("Let me check the files
// first.") belongs with the steps, not with the answer.
export function isNarration(it) {
  const t = (it.content || '').trim();
  return it.role === 'assistant' && t.length <= 280 && !/\n\s*\n|```|^\s*[|#>]|!\[|\$\$/m.test(t);
}

// Reasoning, tool calls and the narration between them form one "steps" group; the
// answer, cards and notes stand alone. Narration only joins a group when more steps
// follow it — the agent's last words are the answer, however short.
export function toSegments(agentItems) {
  const segs = [];
  const laterStep = new Array(agentItems.length).fill(false);
  for (let i = agentItems.length - 2, seen = false; i >= 0; i--) {
    const nx = agentItems[i + 1];
    seen = seen || nx.role === 'tool' || nx.role === 'reasoning';
    laterStep[i] = seen;
  }
  for (let i = 0; i < agentItems.length; i++) {
    const it = agentItems[i];
    const stepish = it.role === 'tool' || it.role === 'reasoning' || (isNarration(it) && laterStep[i]);
    const last = segs[segs.length - 1];
    if (stepish) {
      if (last && last.type === 'steps') last.items.push(it);
      else segs.push({ type: 'steps', key: 'g' + it.key, items: [it] });
    } else {
      segs.push({ type: it.role, key: it.key, item: it });
    }
  }
  return segs;
}

export function stepsDuration(items) {
  let start = Infinity, end = 0;
  for (const it of items) {
    if (it.ts) start = Math.min(start, it.ts);
    const e = it.role === 'tool' ? (it.ts || 0) + (it.elapsed_ms || 0) : (it.t1 || it.ts || 0);
    end = Math.max(end, e);
  }
  return start === Infinity || !end ? 0 : Math.max(0, end - start);
}

export function fmtDuration(ms) {
  const s = Math.round(ms / 1000);
  if (s < 1) return 'under a second';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

export function fmtSize(n) {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
